import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import api from "../services/api";
import {
  ReceiptLong,
  CheckCircle,
  Pending,
  Send,
  Refresh,
} from "@mui/icons-material";

export default function PayrollStatus() {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get("/payroll/status");
      setStatuses(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to fetch payroll status", err);
      toast.error("Failed to load payroll status");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "bg-yellow-100 text-yellow-700",
      generated: "bg-blue-100 text-blue-700",
      sent: "bg-green-100 text-green-700",
    };
    return badges[status] || badges.pending;
  };

  const getStatusIcon = (status) => {
    if (status === "sent") return <CheckCircle className="w-5 h-5" />;
    if (status === "generated") return <ReceiptLong className="w-5 h-5" />;
    return <Pending className="w-5 h-5" />;
  };

  const formatMonth = (month) => {
    const [year, monthNum] = month.split("-");
    const date = new Date(year, parseInt(monthNum) - 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex items-center justify-between"
      >
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2">
            Payroll Processing Status
          </h1>
          <p className="text-gray-600 text-lg">
            Track monthly salary slip generation and distribution
          </p>
        </div>
        <button
          onClick={fetchStatus}
          className="btn-secondary flex items-center gap-2"
        >
          <Refresh className="w-5 h-5" />
          Refresh
        </button>
      </motion.div>

      {/* Status Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      ) : statuses.length === 0 ? (
        <div className="card text-center py-12">
          <ReceiptLong className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg mb-4">
            No payroll processing records found
          </p>
          <p className="text-sm text-gray-500">
            Payroll will be processed automatically on the 1st of each month
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statuses.map((status, index) => (
            <motion.div
              key={status._id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="card hover:shadow-soft-lg transition-all"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-primary">
                  {formatMonth(status.month)}
                </h3>
                <span
                  className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 ${getStatusBadge(
                    status.status
                  )}`}
                >
                  {getStatusIcon(status.status)}
                  {status.status.toUpperCase()}
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Employees:</span>
                  <span className="font-semibold">
                    {status.total_employees}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Slips Generated:</span>
                  <span className="font-semibold text-blue-600">
                    {status.slips_generated}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Slips Sent:</span>
                  <span className="font-semibold text-green-600">
                    {status.slips_sent}
                  </span>
                </div>
                {status.slips_failed > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Failed:</span>
                    <span className="font-semibold text-red-600">
                      {status.slips_failed}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                {status.generated_at && (
                  <div className="text-xs text-gray-500 mb-1">
                    Generated: {new Date(status.generated_at).toLocaleString()}
                  </div>
                )}
                {status.sent_at && (
                  <div className="text-xs text-gray-500">
                    Sent: {new Date(status.sent_at).toLocaleString()}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
